import { useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Widget } from '@components/index';
import { getFreshRecipeImage } from '@constants/common';
import { KTSVG, toAbsoluteUrl } from '@helpers/index';
import { formatMoney, freshProductTitle } from '@helpers/index';
import { getCurrentOrderSelector } from '@modules/order-schedule/core/selectors';
import {
  getProductSelector,
  getVariantProductInfoSelector,
} from '@modules/products/core/selectors';
import { Discount } from '@modules/subscriptions/components/discount/Discount';
import { formatDiscountTitle } from '@modules/subscriptions/core/discountLabel';
import { getCurrencyCodeSelector } from '@modules/subscriptions/core/selectors';
import {
  getItemDiscountsByVariantSelector,
  getStoreCreditByVariantSelector,
} from '@modules/subscriptions/core/selectors';
import { calcDeliveryDate } from '@modules/subscriptions/utils/calcDeliveryDate';
import { formatCycleInterval } from '@modules/subscriptions/utils/formatCycle';
import { filterAdditionalLines, findFreshLine } from '@modules/subscriptions/utils/lineItems';
import { creditForItems, creditForLine } from '@modules/subscriptions/utils/storeCredit';

const toUnitNumber = (value: number | string | undefined | null): number => {
  if (value === undefined || value === null || value === '') return 0;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const lineQuantity = (item: ILineItem): number => {
  const q = item.quantity;
  return Number.isFinite(q) && q > 0 ? q : 1;
};

const getVariantLabel = (item: ILineItem): string | null => {
  const variant = item.variantTitle?.trim();
  if (!variant || variant.toLowerCase() === 'default title') return null;
  return variant;
};

const sumDiscounted = (items: ILineItem[]) =>
  items.reduce((acc, item) => {
    const raw = item.discountedPrice ?? item.price ?? '0';
    const value = toUnitNumber(raw);
    return acc + (item.discountedPrice != null ? value : value * lineQuantity(item));
  }, 0);

const sumBase = (items: ILineItem[]) =>
  items.reduce((acc, item) => {
    const rawUnit = item.basePrice ?? item.price ?? item.discountedPrice ?? '0';
    return acc + toUnitNumber(rawUnit) * lineQuantity(item);
  }, 0);

type GroupedLine = {
  key: string;
  representative: ILineItem;
  totalQuantity: number;
};

const groupKeyFor = (item: ILineItem): string => {
  return [item.variantShopifyId ?? '', item.productShopifyId ?? '', item.variantTitle ?? '']
    .map((v) => String(v ?? ''))
    .join('|');
};

const groupLines = (items: ILineItem[]): GroupedLine[] => {
  const groups = new Map<string, GroupedLine>();
  for (const item of items) {
    const key = groupKeyFor(item);
    const qty = lineQuantity(item);
    const existing = groups.get(key);
    if (existing) {
      existing.totalQuantity += qty;
    } else {
      groups.set(key, { key, representative: item, totalQuantity: qty });
    }
  }
  return Array.from(groups.values());
};

const MAX_VISIBLE_ADDITIONALS = 3;

export const RecipeOverview = () => {
  const intl = useIntl();
  const order = useSelector(getCurrentOrderSelector);
  const currencyCode = useSelector(getCurrencyCodeSelector);

  const formatPrice = (value: number, code: string) => formatMoney(value, code, intl.locale);
  const storeCreditByVariant = useSelector(getStoreCreditByVariantSelector);
  const itemDiscountsByVariant = useSelector(getItemDiscountsByVariantSelector);
  const variantProductInfo = useSelector(getVariantProductInfoSelector);
  const product = useSelector(getProductSelector);

  const [isAdditionalsExpanded, setIsAdditionalsExpanded] = useState(false);

  if (!order) {
    return (
      <Widget
        header={
          <div className="recipe-overview__header">
            <Link to="/orders">
              <span className="recipe-overview__header-link">
                <FormattedMessage
                  id="ORDERS.CURRENT.HEADING"
                  values={{
                    lead: (chunks) => <span>{chunks}</span>,
                    highlight: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              </span>
            </Link>
            <KTSVG path={toAbsoluteUrl('/media/svg/general/hb-arrow-new.svg')} />
          </div>
        }>
        <div className="recipe-overview__empty">
          <FormattedMessage id="ORDERS.NONE_OPEN" />
        </div>
      </Widget>
    );
  }

  const freshLine = findFreshLine(order?.lines);
  const additionalItems = filterAdditionalLines(order?.lines);
  const groupedAdditionals = groupLines(additionalItems);
  const visibleAdditionals = isAdditionalsExpanded
    ? groupedAdditionals
    : groupedAdditionals.slice(0, MAX_VISIBLE_ADDITIONALS);
  const overflowCount = Math.max(0, groupedAdditionals.length - MAX_VISIBLE_ADDITIONALS);
  const canToggleAdditionals = overflowCount > 0;

  const sachets: ISachet[] = variantProductInfo?.sachets || [];
  const cycle = variantProductInfo?.options?.cycle;
  const pension = variantProductInfo?.options?.pension;

  // The pack shot depends on which recipes are in the box, so prefer the
  // combination-specific image and fall back to the Shopify product image.
  const productImg = getFreshRecipeImage(sachets) ?? product?.images?.[0]?.src;

  const [dateFrom, dateTo] = calcDeliveryDate(order?.billingDate);

  // Pricing
  const freshDiscounted = toUnitNumber(freshLine?.discountedPrice ?? freshLine?.price);
  const freshBase =
    freshLine?.basePrice != null ? toUnitNumber(freshLine.basePrice) : freshDiscounted;
  const additionalsTotal = sumDiscounted(additionalItems);
  const additionalsBase = sumBase(additionalItems);

  const additionalsNet = additionalsTotal;

  const computedTotal = freshDiscounted + additionalsNet;
  const computedOriginal = freshBase + additionalsBase;
  const totalHasDiscount = computedOriginal > computedTotal;
  const hasAdditionals = additionalItems.length > 0;

  return (
    <Widget
      header={
        <div className="recipe-overview__header">
          <Link to="/orders">
            <span className="recipe-overview__header-link">
              <FormattedMessage
                id="ORDERS.CURRENT.HEADING"
                values={{
                  lead: (chunks) => <span>{chunks}</span>,
                  highlight: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </span>
          </Link>
          <KTSVG path={toAbsoluteUrl('/media/svg/general/hb-arrow-new.svg')} />
        </div>
      }
      footer={
        <div className="recipe-overview__summary">
          {hasAdditionals && (
            <ul className="recipe-overview__breakdown">
              <li className="recipe-overview__breakdown-row">
                <span className="recipe-overview__breakdown-label">
                  <FormattedMessage id="SUBSCRIPTION.FRESH_FOOD.TITLE" />
                </span>
                <span className="recipe-overview__breakdown-value">
                  {freshBase > freshDiscounted && (
                    <span className="recipe-overview__breakdown-old">
                      {formatPrice(freshBase, currencyCode)}
                    </span>
                  )}
                  <span className="recipe-overview__breakdown-current">
                    {formatPrice(freshDiscounted, currencyCode)}
                  </span>
                </span>
              </li>
              <li className="recipe-overview__breakdown-row">
                <span className="recipe-overview__breakdown-label">
                  <FormattedMessage id="SUBSCRIPTION.EXTRAS.TITLE" />
                </span>
                <span className="recipe-overview__breakdown-value">
                  {additionalsBase > additionalsNet && (
                    <span className="recipe-overview__breakdown-old">
                      {formatPrice(additionalsBase, currencyCode)}
                    </span>
                  )}
                  <span className="recipe-overview__breakdown-current">
                    {formatPrice(additionalsNet, currencyCode)}
                  </span>
                </span>
              </li>
            </ul>
          )}
          <div className="recipe-overview__total">
            <span className="recipe-overview__total-label">
              <FormattedMessage id="RECIPE.OVERVIEW.TOTAL" />
            </span>
            {totalHasDiscount && (
              <span className="recipe-overview__total-old">
                {formatPrice(computedOriginal, currencyCode)}
              </span>
            )}
            <span className="recipe-overview__total-current">
              {formatPrice(computedTotal, currencyCode)}
            </span>
          </div>
        </div>
      }
      footerBg="bg-primary-light">
      <div className="recipe-overview">
        {freshLine && (
          <div className="recipe-overview__fresh">
            <span className="recipe-overview__badge">
              <FormattedMessage id="KCAL.FRESH_FOOD" />
            </span>
            <div className="recipe-overview__fresh-image">
              {productImg ? (
                <img src={productImg} alt={freshProductTitle(freshLine, intl)} />
              ) : null}
            </div>
            <div className="recipe-overview__fresh-body">
              <h4 className="recipe-overview__fresh-title">{freshProductTitle(freshLine, intl)}</h4>
              <div className="recipe-overview__chips">
                {cycle && (
                  <span className="recipe-overview__chip">
                    <KTSVG
                      className="recipe-overview__chip-icon"
                      path={toAbsoluteUrl('/media/svg/general/checkmark.svg')}
                    />
                    {intl.formatMessage(
                      { id: 'CYCLE.EVERY' },
                      { cycle: formatCycleInterval(intl, Number(cycle)) },
                    )}
                  </span>
                )}
                {pension && (
                  <span className="recipe-overview__chip">
                    <KTSVG
                      className="recipe-overview__chip-icon"
                      path={toAbsoluteUrl('/media/svg/general/checkmark.svg')}
                    />
                    {intl.formatMessage({
                      id:
                        pension === 'V' ? 'SUBSCRIPTION.PENSION.FULL' : 'SUBSCRIPTION.PENSION.HALF',
                    })}
                  </span>
                )}
              </div>
            </div>
            <div className="recipe-overview__fresh-price">
              {freshBase > freshDiscounted && (
                <span className="recipe-overview__fresh-price-old">
                  {formatPrice(freshBase, currencyCode)}
                </span>
              )}
              <span className="recipe-overview__fresh-price-current">
                {formatPrice(freshDiscounted, currencyCode)}
              </span>
            </div>
          </div>
        )}

        {hasAdditionals && (
          <div className="recipe-overview__additionals">
            <div className="recipe-overview__additionals-header">
              <span className="recipe-overview__additionals-title">
                <FormattedMessage id="SUBSCRIPTION.EXTRAS.TITLE" />
              </span>
            </div>
            <ul className="recipe-overview__additionals-list">
              {visibleAdditionals.map((group) => {
                const item = group.representative;
                const totalQty = group.totalQuantity;
                const linePrice =
                  item.discountedPrice != null
                    ? toUnitNumber(item.discountedPrice)
                    : toUnitNumber(item.price) * totalQty;
                const basePrice = toUnitNumber(item.basePrice ?? item.price);
                const lineBase = basePrice * totalQty;
                const lineCredit = creditForLine(
                  item.variantShopifyId,
                  linePrice,
                  storeCreditByVariant,
                );
                const finalLinePrice = linePrice;
                const hasDiscount = lineBase > finalLinePrice;
                const variantLabel = getVariantLabel(item);
                const lineDiscounts = itemDiscountsByVariant[String(item.variantShopifyId)] ?? [];
                return (
                  <li key={group.key} className="recipe-overview__additional">
                    <div className="recipe-overview__additional-image">
                      {item.image ? (
                        <img src={item.image} alt={item.productTitle} loading="lazy" />
                      ) : null}
                    </div>
                    <div className="recipe-overview__additional-body">
                      <span className="recipe-overview__additional-name" title={item.productTitle}>
                        {item.productTitle}
                      </span>
                      <div className="recipe-overview__additional-meta">
                        {variantLabel && (
                          <span className="recipe-overview__additional-variant">
                            {variantLabel}
                          </span>
                        )}
                        {totalQty > 1 && (
                          <span className="recipe-overview__additional-quantity">× {totalQty}</span>
                        )}
                      </div>
                      {lineDiscounts.length > 0 && (
                        <div className="recipe-overview__additional-discounts">
                          {lineDiscounts.map((discount) => (
                            <span
                              key={discount.shopifyId ?? discount.title}
                              className="recipe-overview__additional-discount">
                              {formatDiscountTitle(intl, discount.title, currencyCode)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="recipe-overview__additional-price">
                      {hasDiscount && (
                        <span className="recipe-overview__additional-price-old">
                          {formatPrice(lineBase, currencyCode)}
                        </span>
                      )}
                      {formatPrice(finalLinePrice, currencyCode)}
                    </span>
                  </li>
                );
              })}
            </ul>
            {canToggleAdditionals && (
              <button
                type="button"
                className="recipe-overview__additionals-toggle"
                onClick={() => setIsAdditionalsExpanded((prev) => !prev)}
                aria-expanded={isAdditionalsExpanded}>
                {isAdditionalsExpanded
                  ? intl.formatMessage({ id: 'GLOBAL.SHOW_LESS' })
                  : intl.formatMessage({ id: 'GLOBAL.SHOW_MORE' }, { count: overflowCount })}
              </button>
            )}
          </div>
        )}

        {!hasAdditionals && (
          <Link to="/mitbestellen" className="recipe-overview__cta">
            <div className="recipe-overview__cta-icon">
              <KTSVG path={toAbsoluteUrl('/media/svg/general/plus.svg')} />
            </div>
            <div className="recipe-overview__cta-body">
              <span className="recipe-overview__cta-title">
                <FormattedMessage id="SHOP.CTA.ADD_EXTRAS" />
              </span>
              <span className="recipe-overview__cta-subtitle">
                <FormattedMessage id="SHOP.CTA.ADD_EXTRAS_SUBTITLE" />
              </span>
            </div>
          </Link>
        )}

        <div className="recipe-overview__delivery">
          <div className="recipe-overview__delivery-icon">
            <KTSVG path={toAbsoluteUrl('/media/svg/orders/delivery-rounded.svg')} />
          </div>
          <div className="recipe-overview__delivery-content">
            <span className="recipe-overview__delivery-label">
              <FormattedMessage id="RECIPE.OVERVIEW.DELIVERY_LABEL" />
            </span>
            <span className="recipe-overview__delivery-date">
              <FormattedDate value={dateFrom} day="2-digit" /> –{' '}
              <FormattedDate value={dateTo} year="numeric" month="short" day="2-digit" />
            </span>
          </div>
        </div>

        <div className="recipe-overview__discount">
          <Discount disabled={order?.status === 'PROCESSED'} />
        </div>
      </div>
    </Widget>
  );
};
